import { Button } from "@/components/flexkit/Button";
import { Tooltip } from "@/components/Tooltip";
import { useMemo, forwardRef } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useListMCPIntegrations } from "@/queries/integration-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { IconIntegrations } from "@/assets/icons/geist/IconIntegrations";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { AlertTriangle } from "lucide-react";

type MCPIntegrationsButtonProps = {
    agentExecution: PlainAgentExecution;
    organizationId?: string;
} & PropsWithClassName;

export const MCPIntegrationsButton = forwardRef<HTMLButtonElement, MCPIntegrationsButtonProps>(
    function MCPIntegrationsButton({ className, agentExecution, organizationId, ...rest }, ref) {
        const { data: integrations } = useListMCPIntegrations(organizationId);
        const { data: authTokensData } = useListHostAuthenticationTokens({ enabled: true });
        const issueCount = useMemo(() => {
            if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return 0;

            const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
            const authTokenIntegrationIds = new Set(
                authTokensData.tokens.map((token) => token.integrationId).filter((s) => !!s),
            );

            // Only count integrations that have auth tokens and have errors/warnings
            return integrations.filter((integration) => {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                const hasAuthToken = authTokenIntegrationIds.has(integration.id);
                return hasAuthToken && !!(status?.failureMessage || status?.warningMessage);
            }).length;
        }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses]);

        return (
            <Tooltip content="MCP Integrations">
                <div className="relative flex items-center">
                    <Button
                        ref={ref}
                        variant="ghost"
                        LeadingIcon={IconIntegrations}
                        className={cn(
                            "flex items-center justify-center rounded-lg p-2 text-content-secondary hover:bg-surface-button-clear-accent hover:text-content-accent disabled:cursor-not-allowed disabled:opacity-50",
                            className,
                        )}
                        aria-label="MCP Integrations"
                        data-tracking-id="mcp-integrations-button"
                        {...rest}
                    />
                    {issueCount > 0 && (
                        <div className="flex items-center gap-0.5 text-yellow-600 dark:text-yellow-400">
                            <AlertTriangle size={14} />
                            <span className="text-xs font-medium">{issueCount}</span>
                        </div>
                    )}
                </div>
            </Tooltip>
        );
    },
);
